/* Feb-2026 · Token Usage Nudge Banner
 *
 * 75–89 % utilisation  → compact amber warning with "Get Tokens" button.
 * ≥ 90 % utilisation   → urgent rose banner expands to show all 3 token
 *                         packs inline so the user can buy with one click.
 *
 * Rules:
 *  - Only shown to capped-plan users (not BYOK, not unlimited).
 *  - Dismissed per calendar-month via localStorage.
 *  - Pack list fetched lazily only when the urgent tier triggers.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Zap, X, ArrowRight, Loader2, PackageCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usageApi } from '@/lib/api';
import { paymentsApi } from '@/lib/auth_api';

const WARN_PCT   = 75;
const URGENT_PCT = 90;

// ── localStorage dismiss key resets each calendar month ──────────────────
function dismissKey() {
  const d = new Date();
  return `token_nudge_dismissed_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function readDismissed() {
  try { return !!window.localStorage.getItem(dismissKey()); } catch { return false; }
}
function writeDismissed() {
  try { window.localStorage.setItem(dismissKey(), '1'); } catch { /* noop */ }
}

// ── helpers ───────────────────────────────────────────────────────────────
function fmtM(tokens) {
  if (!tokens) return '?';
  const m = tokens / 1_000_000;
  return m >= 1 ? `${m % 1 === 0 ? m : m.toFixed(1)} M` : `${Math.round(tokens / 1_000)} K`;
}

// ── Pack card (urgent state only) ─────────────────────────────────────────
function PackCard({ pack, buying, onBuy }) {
  const isBuying = buying === pack.id;
  return (
    <button
      onClick={() => onBuy(pack.id)}
      disabled={!!buying}
      data-testid={`token-pack-card-${pack.id}`}
      className={[
        'relative flex flex-col items-center rounded-md border px-3 py-2 text-left transition-all',
        'min-w-[88px] shrink-0',
        pack.best
          ? 'border-rose-400/60 bg-rose-500/10 ring-1 ring-rose-400/30'
          : 'border-neutral-600/30 bg-neutral-800/30 hover:border-neutral-500/50',
        isBuying ? 'opacity-70 cursor-wait' : 'cursor-pointer hover:scale-[1.02]',
      ].join(' ')}
    >
      {pack.best && (
        <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-rose-500 px-1.5 py-0 text-[9px] font-bold uppercase tracking-wide text-white">
          popular
        </span>
      )}
      <span className="text-[13px] font-semibold text-foreground">{fmtM(pack.tokens)}</span>
      <span className="text-[13px] text-muted-foreground">tokens</span>
      <span className="mt-1 text-[13.5px] font-bold text-foreground">${pack.price}</span>
      <span className="mt-1.5 w-full rounded bg-rose-500/80 py-0.5 text-center text-[13px] font-semibold text-white">
        {isBuying ? <Loader2 className="mx-auto h-3 w-3 animate-spin" /> : 'Buy'}
      </span>
    </button>
  );
}

// ── Main banner ───────────────────────────────────────────────────────────
export default function TokenNudgeBanner({ onBuyTokens }) {
  const [usage,     setUsage]     = useState(null);
  const [packs,     setPacks]     = useState(null);
  const [buying,    setBuying]    = useState(null);   // pack id currently in checkout
  const [dismissed, setDismissed] = useState(readDismissed);

  // fetch usage once on mount (skip if already dismissed)
  useEffect(() => {
    if (dismissed) return;
    usageApi.me().then(setUsage).catch(() => null);
  }, [dismissed]);

  // ── checkout handler — declared before any early return (Rules of Hooks)
  const handleBuy = useCallback(async (packId) => {
    setBuying(packId);
    try {
      const result = await paymentsApi.checkoutPackage(packId, window.location.href);
      const url = result?.checkout_url || result?.url;
      if (url) window.location.href = url;
    } catch {
      setBuying(null);
    }
  }, []);

  // ── lazy-fetch packs once usage is known and urgent threshold is met ──
  useEffect(() => {
    if (!usage || dismissed) return;
    const { is_byok = false, llm_cap_cents = -1, llm_spent_cents = 0,
            tokens_monthly = 0, llm_tokens_this_month = 0, plan_id = '' } = usage;
    if (is_byok || llm_cap_cents <= 0) return;
    const pct = tokens_monthly > 0
      ? Math.min(100, Math.round((llm_tokens_this_month / tokens_monthly) * 100))
      : Math.min(100, Math.round((llm_spent_cents / llm_cap_cents) * 100));
    if (pct < URGENT_PCT) return;
    paymentsApi.packages()
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.packages ?? data?.pricing ?? []);
        // Personalise: lead with the tier-matched pack, then ascending price
        const TIER_LEAD = { author: 'starter', publisher: 'pro', studio: 'studio' };
        const leadId = TIER_LEAD[plan_id] || 'pro';
        const sorted = list
          .slice()
          .sort((a, b) => {
            if (a.id === leadId) return -1;
            if (b.id === leadId) return 1;
            return a.price - b.price;
          })
          .slice(0, 3);
        setPacks(sorted);
      })
      .catch(() => setPacks([]));
  }, [usage, dismissed]); // packs intentionally excluded — fetch once per session

  // derive metrics — early return after all hooks are declared
  if (dismissed || !usage) return null;
  const { llm_spent_cents = 0, llm_cap_cents = -1, is_byok = false,
          llm_tokens_this_month = 0, tokens_monthly = 0, plan_id = '' } = usage;

  if (is_byok || llm_cap_cents <= 0) return null;

  const useTokens = tokens_monthly > 0;
  const pct = useTokens
    ? Math.min(100, Math.round((llm_tokens_this_month / tokens_monthly) * 100))
    : Math.min(100, Math.round((llm_spent_cents / llm_cap_cents) * 100));

  if (pct < WARN_PCT) return null;

  const isUrgent   = pct >= URGENT_PCT;
  const allowLabel = useTokens ? fmtM(tokens_monthly) : `$${(llm_cap_cents / 100).toFixed(2)}`;
  const usedLabel  = useTokens ? fmtM(llm_tokens_this_month) : null;
  const unitLabel  = useTokens ? 'monthly tokens' : 'monthly AI budget';

  const headline = isUrgent
    ? `You've used ${pct}% of your ${unitLabel} — running low`
    : `You've used ${pct}% of your ${unitLabel}`;
  const subline = usedLabel
    ? `${usedLabel} of ${allowLabel} used.`
    : `${pct}% of your ${allowLabel} used.`;


  const handleDismiss = () => { writeDismissed(); setDismissed(true); };

  // ── theming ────────────────────────────────────────────────────────────
  const border = isUrgent ? 'border-rose-500/40'  : 'border-amber-500/40';
  const bg     = isUrgent ? 'bg-rose-500/[0.06]'  : 'bg-amber-500/[0.06]';
  const icon   = isUrgent ? 'text-rose-500'        : 'text-amber-500';
  const head   = isUrgent ? 'text-rose-500'        : 'text-amber-500';

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div
      className={`my-3 rounded-md border ${border} ${bg} px-3 py-2.5 shadow-sm`}
      role="status"
      data-testid="token-nudge-banner"
    >
      {/* top row — headline + dismiss */}
      <div className="flex items-center gap-2">
        <Zap className={`h-4 w-4 shrink-0 ${icon}`} />
        <div className="min-w-0 flex-1">
          <span className={`text-[14px] font-semibold ${head}`} data-testid="token-nudge-headline">
            {headline}
          </span>
          <span className="ml-2 text-[13.5px] text-muted-foreground" data-testid="token-nudge-body">
            {subline}{' '}
            {!isUrgent && 'Top up to keep writing uninterrupted.'}
          </span>
        </div>

        {/* compact state (warning only) — single CTA */}
        {!isUrgent && (
          <Button
            size="sm"
            variant="outline"
            onClick={onBuyTokens}
            className="h-7 shrink-0 gap-1 px-2 text-[13px]"
            data-testid="token-nudge-buy-btn"
          >
            Get Tokens <ArrowRight className="h-3 w-3" />
          </Button>
        )}

        <Button
          size="sm"
          variant="ghost"
          onClick={handleDismiss}
          className="h-7 shrink-0 px-1.5"
          aria-label="Dismiss token nudge"
          data-testid="token-nudge-dismiss-btn"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      {/* urgent state — inline pack grid */}
      {isUrgent && (
        <div className="mt-2.5" data-testid="token-pack-grid">
          {packs === null && (
            <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading packs…
            </div>
          )}
          {packs !== null && packs.length === 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={onBuyTokens}
              className="h-7 gap-1 px-3 text-[13px]"
              data-testid="token-nudge-buy-btn"
            >
              <PackageCheck className="h-3.5 w-3.5" /> Get more tokens
            </Button>
          )}
          {packs !== null && packs.length > 0 && (
            <div className="flex flex-wrap items-start gap-2">
              {packs.map(pack => (
                <PackCard
                  key={pack.id}
                  pack={pack}
                  buying={buying}
                  onBuy={handleBuy}
                />
              ))}
              <div className="flex flex-col justify-center pl-1 text-[13px] text-muted-foreground">
                <span>Stacks on top of</span>
                <span>your monthly plan.</span>
                <span>Never expires.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
